import { Dispatch } from 'react';
import isObject from 'lodash.isobject';

import cloneDeep from 'lodash.clonedeep';
import { JsonLogicResult, JsonTree } from '@react-awesome-query-builder/antd';
import { v4 } from 'uuid';
import { notification } from 'antd';
import { IAction } from '../root/i-action';
import { IApiService } from '../../services/api-service';
import { IState } from '../root/state.interface';
import { ILocalStorageService } from '../../services/local-storage-service';
import {
  IGetTenantsListResponse,
  ITenantRulesConfig,
  ITenantRulesConfigSchema,
  IRulesFields,
  IRulesConfig,
  IMermaidSchemaConfig,
  IWrappedSchema,
} from './i-tenant';
import { TenantActionType } from './tenant.enum';
import { TRANSFORMS_KEY, RULES_KEY, IMPORTS_KEY } from './tenant.constants';

export interface ITenantActions {
  getTenantsList: () => Promise<void>;
  getTenantRulesConfig: (tenant: string) => Promise<void>;
  getRulesFields: () => Promise<void>;
  getRulesPseudocode: () => Promise<void>;
  getTenantRulesConfigSchema: () => Promise<void>;
  // eslint-disable-next-line
  saveTenantRulesConfig: (tenant: string, data: any) => Promise<void>;
  // eslint-disable-next-line
  saveZones: (zones: any[], groupId: number | undefined) => Promise<void>;
  saveTenantRules: (rules: JsonTree, jsonLogic: JsonLogicResult) => Promise<void>;
  getMermaidDiagram: (level: string, property: string) => Promise<void>;
  getTenantProperties: () => Promise <void>;
  getSchemaProperty: (property: string) => Promise <void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  updateSchemaProperty: (wrappedSchema: IWrappedSchema, props: any) => Promise<void>;
  addProperty: (name: string) => Promise<void>;
  removeProperty: (name: string) => Promise<void>;
  // eslint-disable-next-line
  getModelTree(): Promise<any>;
}

export class TenantsActions implements ITenantActions {
  protected readonly dispatch: Dispatch<IAction>;

  private readonly apiService: IApiService;

  private readonly state: IState;

  private readonly localStorageService: ILocalStorageService;

  public constructor(
    state: IState,
    apiService: IApiService,
    dispatch: Dispatch<IAction>,
    localStorageService: ILocalStorageService,

  ) {
    this.dispatch = dispatch;
    this.apiService = apiService;
    this.state = state;
    this.localStorageService = localStorageService;
  }

  // eslint-disable-next-line
  getModelTree = async (): Promise<any> => {
    // eslint-disable-next-line
    const tree: any[] = await this.apiService.get({ url: '/platform-manager/api/schemaPropertiesTree' });
    // eslint-disable-next-line
    function visit (data: any) {
      // eslint-disable-next-line
      data.id = v4();
      if (data.children) {
        // eslint-disable-next-line
        data.children.forEach((c: any) => {
          visit(c);
        });
      }
    }

    tree.forEach((n) => visit(n));

    return tree;
  };

  // eslint-disable-next-line consistent-return
  public addProperty = async (name: string) : Promise<void> => {
    const processed = {
      type: 'record',
      namespace: 'com.acceliot.avp.datamodel',
      name,
      fields: [],
    };
    try {
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_REQUEST, payload: null });
      return await this.apiService.put({ url: `/platform-manager/api/tenantProperty/schema.${name}`, data: JSON.stringify(processed, null, 2) });
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_FAIL, payload: error });
    }
  };

  public removeProperty = async (name: string) : Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_REQUEST, payload: null });
      const payload = await this.apiService.delete({
        url: `/platform-manager/api/tenantProperty/${name}`,
      });
      if (payload) {
        this.getTenantProperties();
      } else {
        this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_FAIL, payload });
      }
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: TenantActionType.SCHEMA_PROPERTY_UPDATE_FAIL, payload: error });
    }
  };
  // eslint-disable-next-line
  public updateSchemaProperty = async (wrappedSchema: IWrappedSchema, props: any): Promise<void> => {
    const { name } = wrappedSchema;
    let processed = {};
    const { fieldsTable } = props;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const dataToSave = fieldsTable.map((el: any) => {
      if (el.values && Array.isArray(el.values)) {
        if (el.type === 'enum') {
          return el;
        }
        return { ...el, values: null };
      }
      if (el.values && !Array.isArray(el.values)) {
        if (el.type === 'enum') {
          const tmpValues = [];
          const arrayValues = el.values.split('\n');
          // eslint-disable-next-line no-plusplus
          for (let j = 0; j < arrayValues.length; j++) {
            const val = arrayValues[j];
            tmpValues.push({ key: val, value: val });
          }
          return { ...el, values: tmpValues };
        }
        return { ...el, values: null };
      }
      return el;
    });
    if (name) {
      processed = {
        name,
        fields: dataToSave,
      };
    }
    try {
      this.dispatch({ type: TenantActionType.SCHEMA_CONFIG_SAVE_REQUEST, payload: null });
      await this.apiService.put({ url: `/platform-manager/api/wrappedSchema/${name}`, data: JSON.stringify(processed, null, 2) }).then(() => (this.getSchemaProperty(name)));
      this.dispatch({ type: TenantActionType.SCHEMA_CONFIG_SAVE_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: TenantActionType.SCHEMA_CONFIG_SAVE_FAIL, payload: error });
    }
  };

  public getSchemaProperty = async (property: string): Promise< void > => {
    try {
      this.dispatch({ type: TenantActionType.GET_SCHEMA_PROPERTY_REQUEST, payload: null });
      const payload = await this.apiService.get<string>({ url: `/platform-manager/api/wrappedSchema/${property}` });
      // eslint-disable-next-line max-len
      this.dispatch({ type: TenantActionType.GET_SCHEMA_PROPERTY_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_SCHEMA_PROPERTY_FAIL, payload: error });
    }
  };

  public getTenantProperties = async (): Promise< void > => {
    try {
      this.dispatch({ type: TenantActionType.GET_TENANT_PROPERTIES_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<string[]>({ url: '/platform-manager/api/schemaProperties' });
      // eslint-disable-next-line max-len
      this.dispatch({ type: TenantActionType.GET_TENANT_PROPERTIES_LIST_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_TENANT_PROPERTIES_LIST_FAIL, payload: error });
    }
  };

  public getTenantsList = async (): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_TENANTS_LIST_REQUEST, payload: null });
      const payload = await this.apiService.get<IGetTenantsListResponse>({ url: '/platform-manager/crud/tenants' });
      this.dispatch({ type: TenantActionType.GET_TENANTS_LIST_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_TENANTS_LIST_FAIL, payload: error });
    }
  };

  public getTenantRulesConfig = async (tenant: string): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_TENANT_RULES_REQUEST, payload: null });

      const result = await Promise.all([
        this.apiService.get<ITenantRulesConfigSchema>({ url: '/platform-manager/api/tenantConfigSchema' }),
        this.apiService.get<ITenantRulesConfig>({ url: `/platform-manager/api/tenantConfigByCode/${tenant}` }),
        this.apiService.get<IRulesConfig>({ url: '/platform-manager/api/tenantProperty/rulesTree' }),
      ]);

      const [schema, rulesConfig, rulesNewConfig] = result;
      const { transforms = {}, rules = {} } = schema;
      if (!isObject(rulesConfig)) {
        this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SCHEMA_SUCCESS, payload: schema });
        this.dispatch({
          type: TenantActionType.GET_TENANT_RULES_SUCCESS,
          payload: { imports: [], rules: [], transforms: [] },
        });
      } else {
        // We need to complete properties based on the schema
        /* eslint-disable */
        const completedTransforms = rulesConfig.transforms?.map((t: any, i: number) => {        
          const st: any = transforms[t.name];
          !!st && st.properties?.forEach((p: any) => {
            if (!t.properties.hasOwnProperty(p.name)) {
              t.properties[p.name] = ''; // TODO: maybe we need to modify the value based on the p.type value
            }
          });

          return {
            ...t,
            key: `${TRANSFORMS_KEY}_${i}`,
            sort: !!st ? st.sort : 0,
          };
        });
        rulesConfig.transforms = completedTransforms || [];
        const completedRules = rulesConfig.rules?.map((r: any, i: number) => {        
          const st: any = rules[r.name];
          !!st && st.properties?.forEach((p: any) => {
            if (!r.properties.hasOwnProperty(p.name)) {
              r.properties[p.name] = ''; // TODO: maybe we need to modify the value based on the p.type value
            }
          });

          return {
            ...r,
            key: `${RULES_KEY}_${i}`,
            sort: !!st ? st.sort : 0,
          };
        });
        rulesConfig.rules = completedRules || [];
        
        const { imports } = rulesConfig;
        const processedImports: any[] = [];

        const importKeys = Object.keys(imports);      
        importKeys.forEach((key: string, idx: number) => {
          const value = imports[key];
          const { length } = value.headers;
        
          const data: any[] = [];
          for (let i = 0; i < length; i ++) {
            const row: any = {
              key: `${key}_${i}`,
              header: value.headers[i],
              column: value.columns[i],
              pad: value.pads[i],
            };
            data.push(row);
          }

          processedImports.push({
            key: `${IMPORTS_KEY}_${idx}`,
            name: key,
            data
          });

        });        
        /* eslint-enable */
        rulesConfig.imports = processedImports;
        this.dispatch({ type: TenantActionType.GET_RULES_CONFIG_SUCCESS, payload: rulesNewConfig });
        this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SCHEMA_SUCCESS, payload: schema });
        this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SUCCESS, payload: rulesConfig });
      }
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_TENANT_RULES_FAIL, payload: error });
    }
  };

  public getRulesFields = async (): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_RULES_FIELDS_REQUEST, payload: null });
      // eslint-disable-next-line
      const payload: any = await this.apiService.get<IRulesFields>({ url: '/platform-manager/api/raqb/fields/Asset' });
      const payloadPseudo = await this.apiService.get({ url: '/platform-manager/api/rulesPsuedoCode' });

      // const fields = Object.keys(payload).reduce((a, k) => {
      //   if (k.indexOf('.') > -1) {
      //     return { ...a, [k.replaceAll('.', ':')]: payload[k] };
      //   }
      //   return { ...a, [k]: payload[k] };
      // }, {});

      // eslint-disable-next-line max-len
      this.dispatch({ type: TenantActionType.GET_RULES_PSEUDOCODE_SUCCESS, payload: payloadPseudo });
      // this.dispatch({ type: TenantActionType.GET_RULES_FIELDS_SUCCESS, payload: fields });
      this.dispatch({ type: TenantActionType.GET_RULES_FIELDS_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_RULES_FIELDS_FAIL, payload: error });
    }
  };

  public getRulesPseudocode = async (): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_RULES_PSEUDOCODE_REQUEST, payload: null });
      const payloadPseudo = await this.apiService.get({ url: '/platform-manager/api/rulesPsuedoCode' });
      // eslint-disable-next-line max-len
      this.dispatch({ type: TenantActionType.GET_RULES_PSEUDOCODE_SUCCESS, payload: payloadPseudo });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_RULES_PSEUDOCODE_FAIL, payload: error });
    }
  };

  public getTenantRulesConfigSchema = async (): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SCHEMA_REQUEST, payload: null });
      const payload = await this.apiService.get<ITenantRulesConfigSchema>({ url: '/platform-manager/api/tenantConfigSchema' });
      this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SCHEMA_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_TENANT_RULES_SCHEMA_FAIL, payload: error });
    }
  };

  // eslint-disable-next-line
  public saveTenantRulesConfig = async (tenant: string, data: any): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.TENANT_RULES_CONFIG_SAVE_REQUEST, payload: null });
      const { imports = {}, transforms = [], rules = [] } = data;

      const processed = {
        imports,
        // eslint-disable-next-line
        transforms: transforms.map((t: any) => (
          { name: t.name, apply: t.apply, properties: t.properties }
        )),
        // eslint-disable-next-line
        rules: rules.map((r: any) => ({ name: r.name, apply: r.apply, properties: r.properties })),
      };
      // eslint-disable-next-line
      await this.apiService.put<any, any>({ url: `/platform-manager/api/tenantConfigByCode/${tenant}`, data: processed });

      this.dispatch({ type: TenantActionType.TENANT_RULES_CONFIG_SAVE_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: TenantActionType.TENANT_RULES_CONFIG_SAVE_FAIL, payload: error });
    }
  };

  public getMermaidDiagram = async (level: string, property: string): Promise <void> => {
    try {
      this.dispatch({ type: TenantActionType.GET_MERMAID_DIAGRAM_REQUEST, payload: null });
      const payload = await this.apiService.get<IMermaidSchemaConfig>({ url: `${`/platform-manager/api/mermaidDiagram/${property}?detail=`}${level}` });
      this.dispatch({ type: TenantActionType.GET_MERMAID_DIAGRAM_SUCCESS, payload });
    } catch (error) {
      this.dispatch({ type: TenantActionType.GET_MERMAID_DIAGRAM_FAIL, payload: error });
    }
  };

  public saveTenantRules = async (rules: JsonTree, jsonLogic: JsonLogicResult): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.RULES_CONFIG_SAVE_REQUEST, payload: null });
      // eslint-disable-next-line
      await this.apiService.put<any, any>({ url: '/platform-manager/api/tenantProperty/rulesTree', data: rules });
      await this.apiService.put<unknown, unknown>({ url: '/platform-manager/api/tenantProperty/rulesJsonlogic', data: JSON.stringify(jsonLogic) });
      this.dispatch({ type: TenantActionType.RULES_CONFIG_SAVE_SUCCESS, payload: null });
    } catch (error) {
      this.dispatch({ type: TenantActionType.RULES_CONFIG_SAVE_FAIL, payload: error });
    }
  };

  // eslint-disable-next-line
  public saveZones = async (zones: any[], groupId: number | undefined): Promise<void> => {
    try {
      this.dispatch({ type: TenantActionType.ZONES_SAVE_REQUEST, payload: null });

      const payload = cloneDeep(zones);
      // eslint-disable-next-line
      payload.forEach((z: any) => {
        const properties = z.propertiesAsText || '';
        // eslint-disable-next-line
        z.properties = properties.split('\n');
        const logicalReaders = z.logicalReadersAsText || '';
        // eslint-disable-next-line
        z.logicalReaders = logicalReaders.split('\n');
        const polygons = z.polygonsAsText || '';
        // eslint-disable-next-line
        z.polygons = polygons.split('\n');

        // eslint-disable-next-line
        delete z.propertiesAsText;
        // eslint-disable-next-line
        delete z.logicalReadersAsText;
        // eslint-disable-next-line
        delete z.polygonsAsText;
      });

      const url = groupId ? `/facility-manager/api/zoneTree?group_id=${groupId}` : '/facility-manager/api/zoneTree';

      // eslint-disable-next-line
      await this.apiService.put<any, any>({ url, data: payload });

      this.dispatch({ type: TenantActionType.ZONES_SAVE_SUCCESS, payload: null });
      notification.success({
        message: 'Saved!',
      });
    } catch (error) {
      this.dispatch({ type: TenantActionType.ZONES_SAVE_FAIL, payload: error });
    }
  };
}
